import { useEffect, useRef } from 'react';
import { useWebSocket } from '../contexts/WebSocketContext';
import { getActiveRestaurantId } from '../utils/roleAccess';

interface WsHandlers {
  onTableUpdate?: (msg: any) => void;
  onOrderUpdate?: (msg: any) => void;
  onKitchenUpdate?: (msg: any) => void;
  onNotification?: (msg: any) => void;
}

/**
 * Subscribe to restaurant-scoped WebSocket topics.
 * Automatically unsubscribes on unmount.
 *
 * Topics subscribed based on which handlers are provided:
 *   onTableUpdate   → /topic/restaurant/{rId}/tables
 *   onOrderUpdate   → /topic/restaurant/{rId}/orders
 *   onKitchenUpdate → /topic/restaurant/{rId}/kitchen
 *   onNotification  → /topic/restaurant/{rId}/notifications
 */
export function useRestaurantWebSocket(handlers: WsHandlers) {
  const { subscribe, unsubscribe } = useWebSocket();
  const restaurantId = getActiveRestaurantId();
  const subIdsRef = useRef<string[]>([]);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!restaurantId) return;

    const base = `/topic/restaurant/${restaurantId}`;
    const ids: string[] = [];

    if (handlers.onTableUpdate !== undefined) {
      ids.push(subscribe(`${base}/tables`, (msg) => handlersRef.current.onTableUpdate?.(msg)));
    }
    if (handlers.onOrderUpdate !== undefined) {
      ids.push(subscribe(`${base}/orders`, (msg) => handlersRef.current.onOrderUpdate?.(msg)));
    }
    if (handlers.onKitchenUpdate !== undefined) {
      ids.push(subscribe(`${base}/kitchen`, (msg) => handlersRef.current.onKitchenUpdate?.(msg)));
    }
    if (handlers.onNotification !== undefined) {
      ids.push(subscribe(`${base}/notifications`, (msg) => handlersRef.current.onNotification?.(msg)));
    }

    subIdsRef.current = ids;

    return () => {
      ids.forEach(id => unsubscribe(id));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, subscribe, unsubscribe]);
}
